import { useCallback, useEffect, useState, useMemo } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Upload, Trash2, TrendingUp, TrendingDown, Loader2, Wallet, Landmark, ArrowDownUp } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import { api } from "@/lib/api";
import { toast } from "sonner";

const CATEGORIES = [
  "Tasse","Ricavi","Stipendio","Vitto","Trasporti","Utenze",
  "Attrezzature","Servizi","Salute","Carburante","Assicurazione",
  "Abbigliamento","Casa","Svago","Bonifico","Altro"
];

const CAT_COLORS = {
  Tasse: "bg-secondary/15 text-secondary border-secondary/30",
  Ricavi: "bg-primary/15 text-primary border-primary/30",
  Stipendio: "bg-primary/15 text-primary border-primary/30",
  Vitto: "bg-amber-500/10 text-amber-700 border-amber-500/30",
  Trasporti: "bg-blue-500/10 text-blue-700 border-blue-500/30",
  Utenze: "bg-purple-500/10 text-purple-700 border-purple-500/30",
  Carburante: "bg-red-500/10 text-red-700 border-red-500/30",
  Salute: "bg-pink-500/10 text-pink-700 border-pink-500/30",
};

const euro = (n) => `€ ${Number(n || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const monthLabel = (m) => {
  if (!m) return "";
  const [y, mo] = m.split("-");
  const names = ["Gen","Feb","Mar","Apr","Mag","Giu","Lug","Ago","Set","Ott","Nov","Dic"];
  return `${names[parseInt(mo) - 1]} ${y}`;
};

function TrendChart({ currentMonth }) {
  const [data, setData] = useState([]);
  useEffect(() => {
    const months = [];
    // Fallback per month="all" o valori non validi: usa il mese corrente come base
    let y, m;
    if (currentMonth && /^\d{4}-\d{2}$/.test(currentMonth)) {
      [y, m] = currentMonth.split("-").map(Number);
    } else {
      const now = new Date();
      y = now.getFullYear();
      m = now.getMonth() + 1;
    }
    for (let i = 5; i >= 0; i--) {
      const d = new Date(y, m - 1 - i, 1);
      if (isNaN(d.getTime())) continue;
      months.push(d.toISOString().slice(0, 7));
    }
    Promise.all(months.map((mm) => api.get(`/transactions/summary?month=${mm}`).then((r) => ({
      month: monthLabel(mm).split(" ")[0],
      entrate: r.data.entrate || 0,
      uscite: Math.abs(r.data.uscite || 0),
      tasse: r.data.tasse_pagate || 0,
    })).catch(() => ({ month: monthLabel(mm).split(" ")[0], entrate: 0, uscite: 0, tasse: 0 }))))
      .then(setData);
  }, [currentMonth]);
  return (
    <ResponsiveContainer width="100%" height={240}>
      <BarChart data={data}>
        <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
        <XAxis dataKey="month" fontSize={11} />
        <YAxis fontSize={11} />
        <Tooltip formatter={(v) => `€ ${Number(v).toLocaleString("it-IT")}`} />
        <Legend wrapperStyle={{ fontSize: 12 }} />
        <Bar dataKey="entrate" fill="hsl(155 27% 25%)" radius={[6,6,0,0]} name="Entrate" />
        <Bar dataKey="uscite" fill="hsl(14 69% 58%)" radius={[6,6,0,0]} name="Uscite" />
        <Bar dataKey="tasse" fill="hsl(45 60% 55%)" radius={[6,6,0,0]} name="Tasse" />
      </BarChart>
    </ResponsiveContainer>
  );
}

export default function Transactions() {
  const [txs, setTxs] = useState([]);
  const [summary, setSummary] = useState(null);
  // Fix R1: default "Tutti i mesi" invece del mese corrente (spesso vuoto).
  const [month, setMonth] = useState("all");
  const [category, setCategory] = useState("all");
  const [open, setOpen] = useState(false);
  const [csv, setCsv] = useState("");
  const [bank, setBank] = useState("");
  const [importing, setImporting] = useState(false);

  const load = useCallback(async () => {
    const params = new URLSearchParams();
    if (month && month !== "all") params.set("month", month);
    if (category !== "all") params.set("category", category);
    const summaryMonth = month === "all" ? new Date().toISOString().slice(0, 7) : month;
    const [t, s] = await Promise.all([
      api.get(`/transactions?${params}`),
      api.get(`/transactions/summary?month=${summaryMonth}`),
    ]);
    setTxs(t.data);
    setSummary(s.data);
  }, [month, category]);
  useEffect(() => { load(); }, [load]);

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) { toast.error("Max 10MB"); return; }
    const name = f.name.toLowerCase();
    // CSV/TXT → read as text (mostrato nel textarea per anteprima/editing)
    if (name.endsWith(".csv") || name.endsWith(".txt")) {
      const reader = new FileReader();
      reader.onload = () => setCsv(reader.result);
      reader.readAsText(f, "utf-8");
      return;
    }
    // Excel / PDF → upload diretto via multipart (skip textarea)
    if (name.endsWith(".xlsx") || name.endsWith(".xls") || name.endsWith(".pdf")) {
      setImporting(true);
      try {
        const fd = new FormData();
        fd.append("file", f);
        const r = await api.post("/transactions/import-file", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        toast.success(`${r.data.imported} movimenti importati dal file ${name.split(".").pop().toUpperCase()}`);
        setOpen(false); setCsv(""); setBank("");
        // Fix M10: mostra tutti i movimenti importati
        setMonth("all");
        load();
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Errore analisi del file");
      } finally {
        setImporting(false);
        e.target.value = "";
      }
      return;
    }
    toast.error("Formato non supportato. Usa CSV, Excel (.xlsx/.xls) o PDF.");
    e.target.value = "";
  };

  const doImport = async () => {
    if (!csv.trim()) { toast.error("Incolla o carica un CSV"); return; }
    setImporting(true);
    try {
      const r = await api.post("/transactions/import", { csv_text: csv, bank_name: bank });
      toast.success(`${r.data.imported} movimenti importati`);
      setOpen(false); setCsv(""); setBank("");
      // Fix M10: mostra tutti i movimenti importati (possono essere di mesi diversi)
      setMonth("all");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore import");
    } finally { setImporting(false); }
  };

  const del = async (id) => { await api.delete(`/transactions/${id}`); load(); };
  const delAll = async () => {
    if (!window.confirm("Cancellare TUTTI i movimenti?")) return;
    await api.delete("/transactions"); load(); toast.success("Puliti");
  };

  const catEntries = useMemo(() => {
    if (!summary?.by_category) return [];
    return Object.entries(summary.by_category).sort((a, b) => Math.abs(b[1]) - Math.abs(a[1]));
  }, [summary]);

  // Generate month options (last 12 months)
  const months = useMemo(() => {
    const out = [];
    const now = new Date();
    for (let i = 0; i < 12; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      out.push(d.toISOString().slice(0, 7));
    }
    return out;
  }, []);

  return (
    <div className="space-y-6" data-testid="transactions-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-secondary mb-2 flex items-center gap-2">
            <Landmark className="w-3.5 h-3.5" /> Movimenti bancari
          </div>
          <h1 className="font-heading font-bold text-4xl tracking-tighter">Il tuo conto, categorizzato dall'AI.</h1>
          <p className="text-muted-foreground text-sm mt-2">Carica CSV, Excel o PDF della tua banca: l'AI categorizza automaticamente ogni movimento e traccia i pagamenti fiscali.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-full h-12 px-6" data-testid="tx-import-open"><Upload className="w-4 h-4 mr-2" /> Importa movimenti</Button>
          </DialogTrigger>
          <DialogContent className="rounded-2xl max-w-xl">
            <DialogHeader><DialogTitle className="font-heading">Importa estratto conto</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>File estratto conto (CSV, Excel .xlsx/.xls, o PDF)</Label>
                <Input type="file" accept=".csv,.txt,.xlsx,.xls,.pdf" onChange={onFile} className="h-12 rounded-xl mt-1.5" data-testid="tx-csv-file" />
                <p className="text-xs text-muted-foreground mt-1">Excel e PDF vengono importati direttamente. Per il CSV puoi anche incollarlo qui sotto. Funziona con qualsiasi banca italiana — l'AI riconosce il formato automaticamente.</p>
              </div>
              <div>
                <Label>Banca (opzionale)</Label>
                <Input value={bank} onChange={(e) => setBank(e.target.value)} placeholder="Es. Intesa Sanpaolo" className="h-12 rounded-xl mt-1.5" data-testid="tx-bank-input" />
              </div>
              <div>
                <Label>Oppure incolla il testo</Label>
                <Textarea value={csv} onChange={(e) => setCsv(e.target.value)} rows={6} className="rounded-xl mt-1.5 font-mono text-xs" placeholder="Data;Descrizione;Importo..." data-testid="tx-csv-text" />
              </div>
              <Button onClick={doImport} disabled={importing || !csv.trim()} className="rounded-full w-full h-12" data-testid="tx-import-submit">
                {importing ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Analisi AI in corso...</> : "Importa e categorizza"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters */}
      <div className="flex gap-3 flex-wrap items-center">
        <div className="flex-1 min-w-[180px]">
          <Label className="text-xs text-muted-foreground">Mese</Label>
          <Select value={month} onValueChange={setMonth}>
            <SelectTrigger className="h-10 rounded-xl mt-1" data-testid="tx-month-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti i mesi</SelectItem>
              {months.map((m) => <SelectItem key={m} value={m}>{monthLabel(m)}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex-1 min-w-[180px]">
          <Label className="text-xs text-muted-foreground">Categoria</Label>
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger className="h-10 rounded-xl mt-1" data-testid="tx-cat-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutte</SelectItem>
              {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        {txs.length > 0 && (
          <Button variant="ghost" size="sm" onClick={delAll} className="rounded-full text-muted-foreground mt-6" data-testid="tx-delete-all">
            <Trash2 className="w-3.5 h-3.5 mr-1" /> Svuota
          </Button>
        )}
      </div>

      {/* Summary */}
      {summary && summary.count > 0 && (
        <div className="grid md:grid-cols-4 gap-4" data-testid="tx-summary">
          <Card className="p-5 rounded-2xl border">
            <div className="flex items-center gap-2 text-xs text-muted-foreground mb-2"><TrendingUp className="w-3.5 h-3.5 text-primary" /> Entrate</div>
            <div className="font-heading font-bold text-2xl text-primary">{euro(summary.entrate)}</div>
          </Card>
          <Card className="p-5 rounded-2xl border">
            <div className="flex items-center gap-2 text-xs text-muted-foreground mb-2"><TrendingDown className="w-3.5 h-3.5 text-secondary" /> Uscite</div>
            <div className="font-heading font-bold text-2xl text-secondary">{euro(summary.uscite)}</div>
          </Card>
          <Card className="p-5 rounded-2xl border">
            <div className="flex items-center gap-2 text-xs text-muted-foreground mb-2"><ArrowDownUp className="w-3.5 h-3.5" /> Saldo periodo</div>
            <div className={`font-heading font-bold text-2xl ${summary.saldo_periodo >= 0 ? "text-primary" : "text-secondary"}`}>{euro(summary.saldo_periodo)}</div>
          </Card>
          <Card className="p-5 rounded-2xl border-2 border-secondary/30 bg-secondary/5" data-testid="tx-tasse-card">
            <div className="flex items-center gap-2 text-xs text-muted-foreground mb-2"><Wallet className="w-3.5 h-3.5 text-secondary" /> Tasse versate</div>
            <div className="font-heading font-bold text-2xl text-secondary">{euro(summary.tasse_pagate)}</div>
            <div className="text-[10px] text-muted-foreground mt-1">F24, IRPEF, IVA, INPS</div>
          </Card>
        </div>
      )}

      {/* Category breakdown */}
      {catEntries.length > 0 && (
        <div className="grid lg:grid-cols-3 gap-6">
          <Card className="lg:col-span-2 p-6 rounded-2xl border" data-testid="tx-trend-chart">
            <div className="font-heading font-semibold text-lg mb-4">Trend ultimi 6 mesi</div>
            <TrendChart currentMonth={month} />
          </Card>
          <Card className="p-6 rounded-2xl border" data-testid="tx-category-breakdown">
            <div className="font-heading font-semibold text-lg mb-4">Categorie — {monthLabel(month)}</div>
            <div className="space-y-3">
              {catEntries.slice(0, 6).map(([cat, val]) => {
                const isEntrata = val >= 0;
                const pct = summary.uscite === 0 ? 0 : Math.abs(val) / Math.abs(isEntrata ? summary.entrate : summary.uscite) * 100;
                return (
                  <div key={cat} className="flex items-center gap-3">
                    <Badge variant="outline" className={`rounded-full ${CAT_COLORS[cat] || "bg-muted"}`}>{cat}</Badge>
                    <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                      <div className={`h-full ${isEntrata ? "bg-primary" : "bg-secondary"}`} style={{ width: `${Math.min(pct, 100)}%` }} />
                    </div>
                    <div className={`text-sm font-semibold tabular-nums w-24 text-right ${isEntrata ? "text-primary" : "text-secondary"}`}>
                      {isEntrata ? "+" : ""}{euro(val)}
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>
        </div>
      )}

      {/* Transactions list */}
      {txs.length === 0 ? (
        <Card className="p-12 rounded-2xl border-dashed border-2 text-center" data-testid="tx-empty">
          <Landmark className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
          <div className="font-heading font-semibold text-xl mb-1">Nessun movimento</div>
          <div className="text-muted-foreground text-sm mb-4">Scarica il CSV dalla tua home banking e caricalo per iniziare.</div>
        </Card>
      ) : (
        <Card className="rounded-2xl border overflow-hidden" data-testid="tx-list">
          <div className="divide-y">
            {txs.map((t) => (
              <div key={t.id} className="p-4 flex items-center gap-4 hover:bg-accent/30 transition-colors" data-testid={`tx-item-${t.id}`}>
                <div className="text-xs text-muted-foreground tabular-nums w-24 flex-shrink-0">
                  {new Date(t.date).toLocaleDateString("it-IT", { day: "2-digit", month: "short" })}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{t.description}</div>
                  <Badge variant="outline" className={`rounded-full mt-1 text-[10px] ${CAT_COLORS[t.category] || "bg-muted"}`}>{t.category}</Badge>
                </div>
                <div className={`text-sm font-heading font-bold tabular-nums whitespace-nowrap ${t.amount >= 0 ? "text-primary" : "text-secondary"}`}>
                  {t.amount >= 0 ? "+" : ""}{euro(t.amount)}
                </div>
                <Button variant="ghost" size="icon" onClick={() => del(t.id)} className="opacity-30 hover:opacity-100" data-testid={`tx-del-${t.id}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            ))}
          </div>
        </Card>
      )}

      <div className="text-xs text-muted-foreground pt-4">
        I dati restano nel tuo account. Nessuna condivisione con banche o terze parti.
      </div>
    </div>
  );
}
